import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const getMyRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    if (error) throw error;
    const roles = (data ?? []).map((r: { role: string }) => r.role);
    const isAdmin = roles.includes("admin");
    const isAssistant = roles.includes("assistant");
    const isStaff = isAdmin || isAssistant;
    return { roles, isAdmin, isAssistant, isStaff };
  });

export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("profiles")
      .select("id, full_name, phone_whatsapp, phone_whatsapp_legacy, country, city")
      .eq("user_id", context.userId)
      .maybeSingle();
    return data;
  });

export const getAdminKpis = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: rolesRow } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const roles = (rolesRow ?? []).map((r: { role: string }) => r.role);
    if (!roles.includes("admin") && !roles.includes("assistant")) {
      throw new Error("Forbidden");
    }

    const [pending, published, recent, popular] = await Promise.all([
      context.supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .is("deleted_at", null)
        .in("status", ["new", "contacted", "awaiting_payment"])
        .eq("is_test", false),
      context.supabase
        .from("posts")
        .select("id", { count: "exact", head: true })
        .eq("status", "published")
        .is("deleted_at", null),
      context.supabase
        .from("bookings")
        .select("id, status, scheduled_at, created_at, service_id, client_id")
        .is("deleted_at", null)
        .eq("is_test", false)
        .order("created_at", { ascending: false })
        .limit(5),
      context.supabase
        .from("posts")
        .select("id, title_ar, views_count, slug")
        .eq("status", "published")
        .is("deleted_at", null)
        .order("views_count", { ascending: false })
        .limit(5),
    ]);

    // Counts only (no PII on the dashboard). Service role is used after the
    // staff check above because these queues span every buyer.
    const isAdmin = roles.includes("admin");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const pendingReceipts = await (supabaseAdmin as any)
      .from("pending_purchases")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending");

    let smartNeedsAction = 0;
    let smartOverdue = 0;
    if (isAdmin) {
      const { isOverdue } = await import("@/lib/bousala/smart-order");
      const { data: smartRows } = await (supabaseAdmin as any)
        .from("smart_reports")
        .select("status, sla_due_at")
        .in("status", ["payment_pending_review", "intake_complete", "ready_for_review"])
        .limit(500);
      const now = new Date();
      const rows = (smartRows ?? []) as Array<{ status: any; sla_due_at: string | null }>;
      smartNeedsAction = rows.length;
      smartOverdue = rows.filter((r) =>
        isOverdue({ status: r.status, slaDueAt: r.sla_due_at }, now),
      ).length;
    }

    return {
      isAdmin,
      pendingBookings: pending.count ?? 0,
      publishedPosts: published.count ?? 0,
      pendingReceipts: pendingReceipts.count ?? 0,
      smartNeedsAction,
      smartOverdue,
      recentBookings: recent.data ?? [],
      popularPosts: popular.data ?? [],
    };
  });
